import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ChakraProvider, Box, Flex } from '@chakra-ui/react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store from './store';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import ManageCompany from './pages/ManageCompany';
import SuperStokezManagement from './pages/SuperStokezManagement';
import AdminManagement from './pages/AdminManagement';
import StokezManagement from './pages/StokezManagement';
import AgentManagement from './pages/AgentManagement';
import PlayerManagement from './pages/PlayerManagement';
import GameManagement from './pages/GameManagement';
import DrawsRouter from './pages/DrawsRouter';
import CurrentDraws from './pages/CurrentDraws';
import DrawsByGame from './pages/DrawsByGame';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import Login from './components/Login';
import GenericReport from './components/GenericReport';
import AllReporting from './pages/AllReporting';
import ResultControlling from './pages/ResultControlling';
import ResultControllingTktDetails from './pages/ResultControllingTktDetails';
import EngineDebug from './pages/EngineDebug';
import TikcetDetailsNew from './pages/TikcetDetailsNew';
import SearchBarcode from './pages/SearchBarcode'
import GameWiseSaleReport from './pages/GameWiseSaleReport'
import PlayerWiseSaleReport from './pages/PlayerWiseSaleReport';
import SummaryReport from './pages/SummaryReport';
import DateWiseSaleReport from './pages/DateWiseSaleReport';
import ActivityLog from './pages/ActivityLog';
import DrawTicketDetails from './pages/DrawTicketDetails';
import BalanceTransactionReport from './pages/BalanceTransactionReport'
import PrivilegeManagement from './pages/PrivilegeManagement';
import PrivilegeCatalog from './pages/PrivilegeCatalog';
import { getMyPrivileges } from './actions/privilegeActions';
import { isSuperAdminRole } from './utils/auth';
import { ALL_PRIVILEGE_KEYS } from './utils/privilegeCatalog';
import { allFieldsTrueMap } from './utils/fieldCatalog';

const ALL_PANEL_ROLES = ['super_admin', 'admin', 'super_stokez', 'stokez', 'agent'];

const allPrivilegesTrue = () => {
  const map = {};
  ALL_PRIVILEGE_KEYS.forEach((k) => {
    map[k] = true;
  });
  return map;
};

const PrivilegesBootstrap = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user, token } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!isAuthenticated || !token) return;
    // Super admin: force full privilege map in session (no restrictions)
    if (isSuperAdminRole(user?.role)) {
      const privs = user?.privileges || {};
      const incomplete =
        !privs ||
        ALL_PRIVILEGE_KEYS.some((k) => privs[k] !== true);
      if (incomplete) {
        dispatch({ type: 'SET_USER_PRIVILEGES', payload: allPrivilegesTrue() });
      }
      if (!user?.fieldPrivileges) {
        dispatch({ type: 'SET_USER_FIELD_PRIVILEGES', payload: allFieldsTrueMap() });
      }
      return;
    }
    // Refresh once per auth/session role — do NOT depend on user.privileges
    // (getMyPrivileges writes privileges and would infinite-loop).
    dispatch(getMyPrivileges()).catch(() => {});
  }, [dispatch, isAuthenticated, token, user?.role]);

  return null;
};

const PrivateRoute = ({ children, allowedRoles, privilege }) => {
  const { isAuthenticated, user } = useSelector(state => state.auth);
  const params = useParams();
  const location = useLocation();

  const slugFromParams = params?.slug;
  const slugFromPath = location.pathname.split('/').filter(Boolean)[0];
  const fallbackSlug = user?.companySlug || 'root';
  const slug = slugFromParams || slugFromPath || fallbackSlug;

  if (!isAuthenticated || !user?.role) {
    return <Navigate to={`/${slug}/login`} replace />;
  }

  // Super admin: unrestricted access to every route / view / action
  if (isSuperAdminRole(user.role)) {
    return children;
  }

  // When a privilege key is set, that is the access source of truth (Option A).
  // allowedRoles is only a coarse fallback when privilege is omitted.
  if (privilege) {
    const privs = user.privileges || {};
    if (!privs[privilege]) {
      return <Navigate to={`/${slug}/dashboard`} replace />;
    }
  } else if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={`/${slug}/dashboard`} replace />;
  }

  return children;
};

const RedirectUnknownRoute = () => {
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  const slug = pathParts[0] || 'root';
  return <Navigate to={`/${slug}/login`} replace />;
};

const Layout = ({ children }) => {
  const [sidebarWidth, setSidebarWidth] = useState('260px');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const updateSidebarWidth = () => {
      const width = getComputedStyle(document.documentElement)
        .getPropertyValue('--sidebar-width')
        .trim() || '260px';
      setSidebarWidth(width);
    };

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial update
    updateSidebarWidth();
    checkMobile();

    // Check periodically for changes
    const interval = setInterval(updateSidebarWidth, 50);
    window.addEventListener('resize', checkMobile);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Get responsive margin
  const getMarginLeft = () => {
    if (isMobile) {
      return '0px';
    }
    return sidebarWidth;
  };

  return (
    <Flex position="relative" minH="100vh" w="100%">
      <Sidebar />
      <Box 
        flex={1} 
        minWidth={0} 
        overflowX="hidden"
        w="100%"
        style={{ 
          marginLeft: getMarginLeft(),
          transition: 'margin-left 0.3s ease'
        }}
      >
        <Header />
        <Box 
          as="main" 
          p={{ base: 2, md: 4 }}
          w="100%"
          maxW="100%"
          overflowX="hidden"
        >
          {children}
        </Box>
      </Box>
    </Flex>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <ChakraProvider
        toastOptions={{
          defaultOptions: {
            position: 'top',
            duration: 5000,
            isClosable: true,
            containerStyle: { zIndex: 10000 },
          },
        }}
      >
        <Router>
          <PrivilegesBootstrap />
          <Routes>
            <Route path="/:slug/login" element={<Login />} />

            {/* Existing Routes */}
            <Route path="/:slug/dashboard" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent', 'player']} privilege="dashboard.view">
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/root/manage-company" element={
              <PrivateRoute allowedRoles={ALL_PANEL_ROLES} privilege="company.view">
                <Layout>
                  <ManageCompany />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/users" element={
              <PrivateRoute allowedRoles={ALL_PANEL_ROLES} privilege="admin.view">
                <Layout>
                  <AdminManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/super-stokez" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin']} privilege="superStokez.view">
                <Layout>
                  <SuperStokezManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/stokez" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez']} privilege="stokez.view">
                <Layout>
                  <StokezManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/agent" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez']} privilege="agent.view">
                <Layout>
                  <AgentManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/player" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="player.view">
                <Layout>
                  <PlayerManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/games" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin']} privilege="game.manage">
                <Layout>
                  <GameManagement />
                </Layout>
              </PrivateRoute>
            } />

            {/* Sales Report Routes */}
            <Route path="/:slug/reports/summary" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.summary">
                <Layout>
                  <DateWiseSaleReport />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/all-ticket" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.allTicket">
                <Layout>
                  <AllReporting />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/super-stockez" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin']} privilege="report.superStokez">
                <Layout>
                  <GenericReport type="superStockez" />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/stockez/:parentId?" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez']} privilege="report.stokez">
                <Layout>
                  <GenericReport type="stockez" />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/agent/:parentId?" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez']} privilege="report.agent">
                <Layout>
                  <GenericReport type="agent" />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/player/:parentId?" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.player">
                <Layout>
                  <GenericReport type="player" />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/allplayer" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.player">
                <Layout>

                  <PlayerWiseSaleReport />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/all-summary" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.summary">
                <Layout>
                  <SummaryReport />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/game" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.gameWise">
                <Layout>

                  <GameWiseSaleReport />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/search" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.searchBarcode">
                <Layout>
                  <SearchBarcode />
                </Layout>
              </PrivateRoute>
            } />

            {/* Existing Routes */}
            <Route path="/:slug/reports" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.summary">
                <Layout>
                  <Reports />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/settings" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin']} privilege="settings.manage">
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/privileges" element={
              <PrivateRoute
                allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']}
                privilege="privilege.manage"
              >
                <Layout>
                  <PrivilegeManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/privilege-catalog" element={
              <PrivateRoute allowedRoles={['super_admin']}>
                <Layout>
                  <PrivilegeCatalog />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/draws" element={
              <PrivateRoute allowedRoles={ALL_PANEL_ROLES} privilege="draw.manage">
                <Layout>
                  <DrawsRouter />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/current-draws" element={
              <PrivateRoute allowedRoles={ALL_PANEL_ROLES} privilege="draw.current">
                <Layout>
                  <CurrentDraws />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/draws-by-game" element={
              <PrivateRoute allowedRoles={ALL_PANEL_ROLES} privilege="draw.byGame">
                <Layout>
                  <DrawsByGame />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/tktsalereport" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin']} privilege="report.allTicket">
                <Layout>
                  <TikcetDetailsNew />
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/:slug/reports/tktdetails" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.allTicket">
                <Layout>
                  <TikcetDetailsNew />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/:slug/reports/drawtickets" element={
              <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.allTicket">
                <Layout>
                  <DrawTicketDetails />
                </Layout>
              </PrivateRoute>
            } />



            <Route path="/:slug/ds" element={
              <PrivateRoute allowedRoles={ALL_PANEL_ROLES} privilege="result.control">
                <Layout>
                  <ResultControlling />
                </Layout>
              </PrivateRoute>
            } />
              <Route
              path="/:slug/ds/tkt_history"
              element={
                <PrivateRoute allowedRoles={ALL_PANEL_ROLES} privilege="result.control">
                  <Layout>
                    <ResultControllingTktDetails />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/:slug/engine-debug"
              element={
                <PrivateRoute allowedRoles={ALL_PANEL_ROLES} privilege="result.engineDebug">
                  <Layout>
                    <EngineDebug />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/:slug/translog"
              element={
                <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.transaction">
                  <Layout>
                    <BalanceTransactionReport />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/:slug/activity-log"
              element={
                <PrivateRoute allowedRoles={['super_admin', 'admin', 'super_stokez', 'stokez', 'agent']} privilege="report.activityLog">
                  <Layout>
                    <ActivityLog />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route path="*" element={<RedirectUnknownRoute />} />
          </Routes>
        </Router>
      </ChakraProvider>
    </Provider>
  );
};

export default App;